import type { TFunction } from 'i18next';

import type { CatalogSchemas } from '../types/catalog-service.api.types';
import type { StageProgress } from '../types/run.api.types';

import { statusTone } from './run-format';

type StageAttempt = CatalogSchemas['StageAttempt'];

/** The count check a stage ran on finishing ({result, expected, actual, detail}), when it did. */
export interface Reconciliation {
  result: string;
  expected: number | null;
  actual: number | null;
  detail: string | null;
}

/** Narrows the progress API's loosely typed `reconciliation` object. */
export function readReconciliation(value: StageProgress['reconciliation']): Reconciliation | null {
  if (!value || typeof value.result !== 'string') return null;
  const num = (v: unknown) => (typeof v === 'number' ? v : null);
  return {
    result: value.result,
    expected: num(value.expected),
    actual: num(value.actual),
    detail: typeof value.detail === 'string' && value.detail !== '' ? value.detail : null,
  };
}

/** One distinct reason a stage reported, with the attempts that reported it. */
export interface StageReason {
  text: string;
  /** Attempt numbers that carried this exact text, oldest first. */
  attempts: number[];
  /** Error when any attempt carrying it failed; a soft note otherwise. */
  tone: 'error' | 'warning';
}

/** Trimmed, whitespace-collapsed text — two reasons that differ only in spacing are one. */
function normalise(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Every distinct reason behind a stage, each ONCE: the attempts' failure / soft-failure texts plus
 * the stage's own note, identical texts merged ("same reason ×3"), and the run's failure reason
 * left out — the card's failure summary already states it.
 */
export function collectStageReasons(
  stage: StageProgress,
  attempts: StageAttempt[],
  runFailureReason?: string | null,
): StageReason[] {
  const skip = normalise(runFailureReason);
  const byText = new Map<string, StageReason>();
  const add = (raw: string | null | undefined, attempt: number, failed: boolean) => {
    const text = normalise(raw);
    if (!text || text === skip) return;
    const entry = byText.get(text) ?? { text, attempts: [], tone: 'warning' };
    if (!entry.attempts.includes(attempt)) entry.attempts.push(attempt);
    if (failed) entry.tone = 'error';
    byText.set(text, entry);
  };
  for (const a of attempts) {
    const failed = statusTone(a.status) === 'failed';
    add(a.failureReason, a.attempt, failed);
    add(a.softFailure, a.attempt, failed);
  }
  // The stage note is the newest attempt's — it merges into that attempt's identical text.
  add(stage.failureReason, stage.attempt, statusTone(stage.status) === 'failed');
  return [...byText.values()].map((r) => ({ ...r, attempts: r.attempts.sort((x, y) => x - y) }));
}

/** A failure reason reduced to what a person needs, for the plain-words summary line. */
export type PlainReason =
  | { kind: 'gateway'; code: string }
  | { kind: 'timeout' }
  | { kind: 'text'; text: string };

/**
 * Turns a raw stage/run failure text into plain words: drops the "<STAGE> reported FAILED:"
 * prefix and exception class names, and names the common causes (a service error with its HTTP
 * code, a timeout). Anything else is returned as its first sentence, untouched.
 */
export function plainFailureReason(raw: string): PlainReason {
  const text = normalise(raw)
    .replace(/^[A-Z][A-Z_]+ reported [A-Z_]+:\s*/, '')
    .replace(/^[A-Za-z]+(?:Error|Exception):\s*/, '');
  const gateway = /(?:gateway|service|upstream)[^0-9]{0,40}\b([45]\d\d)\b/i.exec(text);
  if (gateway?.[1]) return { kind: 'gateway', code: gateway[1] };
  if (/\btim(?:ed|e)[ -]?out\b|deadline exceeded/i.test(text)) return { kind: 'timeout' };
  return { kind: 'text', text: withoutInternals(text) };
}

/** Internal service names a reason may carry, and the words a person reads instead. */
const SERVICE_WORDS: [RegExp, string][] = [
  [/\bintegration-sync\b/gi, 'the store connection'],
  [
    /\b(?:content-generation|content-gen|agentic-analytics|catalog-service|workflow-webapp)\b/gi,
    'a Lily service',
  ],
];

/**
 * Strips what only engineers read from a reason: internal service names become plain words and an
 * "(HTTP 404)" suffix goes ("integration-sync refused Feed intake (HTTP 404)" → "The store
 * connection couldn't start Feed intake").
 */
function withoutInternals(text: string): string {
  let out = text;
  for (const [pattern, words] of SERVICE_WORDS) out = out.replace(pattern, words);
  out = out.replace(/\s*\(HTTP \d{3}\)/gi, '').trim();
  // "X refused <step>" is a service declining to launch the step: say that, not "refused".
  out = out.replace(/^(the store connection|a Lily service) refused\b/i, "$1 couldn't start");
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/**
 * A raw failure reason as one plain sentence for any list or detail line: service errors and
 * timeouts in words, internal names and HTTP codes stripped, raw stage codes as step names, and
 * a raw id never shown. Pass the run namespace's `t` and the catalog namespace's `tCatalog`.
 */
export function readableReason(
  t: TFunction<'catalog-enrichment-run'>,
  stepNames: (text: string) => string,
  raw: string | null | undefined,
): string | null {
  if (!raw?.trim()) return null;
  // The scheduler's lane-busy skip names an enum, the tenant and the lane: one plain sentence.
  if (/already in flight/i.test(raw)) return t('failureSummary.laneBusy');
  const plain = plainFailureReason(raw);
  if (plain.kind === 'gateway') return t('failureSummary.gatewayPlain');
  if (plain.kind === 'timeout') return t('failureSummary.timeout');
  return (
    stepNames(plain.text)
      // Any other bare enum (FOO_BAR) reads as words.
      .replace(/\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g, (code) =>
        code.toLowerCase().replace(/_/g, ' '),
      )
      .replace(/\s*\(?\b(?:run|batch|job) [0-9a-f]{8}-[0-9a-f-]{27,}[^)]*\)?/gi, '')
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim()
  );
}
